import React, { createContext, useContext, useEffect, useRef } from 'react'
import { App } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import {
  initialPlayerValues,
  mapPlayerToForm,
  submitPlayer,
  toUpdatePayload,
  BE_TO_FIELD_TITLE,
  type EditPlayerFormValues,
} from '@sportninja/common/features/profile/player'
import { PLAYER_SUCCESS_MESSAGES } from '@sportninja/common/features/team/player'
import {
  useMePlayerProfile,
  useUpdateMePlayerProfile,
  useMePlayerImageMutation,
} from './hooks/useMePlayerProfile'
import { scrollToFirstFieldError } from 'src/helpers/scrollToFirstFieldError'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'

export interface EditPlayerContextValue {
  isLoading: boolean
  onCancel: () => void
  /** The player being edited (undefined in a future "Add Player" flow). */
  playerId?: string
  /**
   * Canlan Salesforce ID straight off the profile response — display-only, so
   * it never enters the form. Optional: absent on other tenants, on players
   * without one, and in the roster flow (which supplies its own context value).
   */
  salesforceId?: string | null
}

// Exported so other features (e.g. the roster Add Player modal) can reuse the
// profile section components, which read `useEditPlayerContext`, by supplying
// their own compatible context value. The profile provider/behavior is unchanged.
export const EditPlayerContext = createContext<EditPlayerContextValue | null>(
  null
)

export const useEditPlayerContext = (): EditPlayerContextValue => {
  const ctx = useContext(EditPlayerContext)
  if (!ctx) {
    throw new Error(
      'useEditPlayerContext must be used within an EditPlayerProvider'
    )
  }
  return ctx
}

interface EditPlayerProviderProps {
  onClose: () => void
  onSuccess?: () => void
  /**
   * When provided, the player is fetched (GET /players/:id via React Query) and
   * the form is pre-populated. When absent, the form opens empty and no fetch
   * happens (supports a future "Add Player" entry).
   */
  playerId?: string
  children: React.ReactNode
}

export const EditPlayerProvider: React.FC<EditPlayerProviderProps> = ({
  onClose,
  onSuccess,
  playerId,
  children,
}) => {
  const { message } = App.useApp()
  const { data, loading, error } = useMePlayerProfile(playerId)
  const updatePlayer = useUpdateMePlayerProfile()
  const {
    uploadImage,
    deleteImage,
    isPending: isImagePending,
  } = useMePlayerImageMutation()

  // Keep the latest onClose in a ref so the error effect doesn't re-fire (and
  // double-toast / double-close) if a parent passes a new onClose identity.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Fetch failure → surface a toast and close the modal (exactly once per error).
  useEffect(() => {
    if (error) {
      message.error('Could not load player. Please try again.')
      onCloseRef.current()
    }
  }, [error, message])

  if (error) {
    return null
  }

  // Skeleton shows only during the cold initial fetch (no data yet). A later
  // background refetch (loading true while data is present) must NOT replace the
  // filled form with the skeleton — mirrors the formikKey guard below.
  const isFetchLoading = Boolean(playerId) && loading && !data

  // Formik key: mount as 'loading' only on the initial cold fetch. Once data has
  // arrived the key stays 'ready' for the modal's life — a later background
  // refetch must NOT remount Formik and discard in-progress edits.
  const formikKey = Boolean(playerId) && loading && !data ? 'loading' : 'ready'

  const initialValues = data ? mapPlayerToForm(data) : initialPlayerValues

  const handleSubmit = async (
    values: EditPlayerFormValues,
    helpers: FormikHelpers<EditPlayerFormValues>
  ) => {
    if (!playerId) {
      // No target to save against (e.g. future Add Player path) — just close.
      helpers.setSubmitting(false)
      onSuccess?.()
      onClose()
      return
    }

    // Early-return guard: if nothing changed (no payload diff AND no image
    // change), close silently without calling submitPlayer or showing a toast.
    const payload = toUpdatePayload(values, initialValues)
    const imageChanged = Boolean(values.imageData) || values.imageDeleted
    if (Object.keys(payload).length === 0 && !imageChanged) {
      helpers.setSubmitting(false)
      onClose()
      return
    }

    try {
      // 1. Image step (only if changed). Runs BEFORE the PUT so a failure aborts
      //    the whole save without a partial silent write. The image mutation
      //    invalidates playerProfile.byId + userFamily.me itself.
      if (values.imageData) {
        await uploadImage({ playerId, file: values.imageData })
      } else if (values.imageDeleted) {
        await deleteImage({ playerId })
      }

      // 2. Image-only save (image changed but no field diff): submitPlayer would
      //    skip the PUT and its success toast, so surface the toast here to keep
      //    the original behavior (avatar-only saves still confirm + close).
      if (imageChanged && Object.keys(payload).length === 0) {
        message.success(formatSuccessToast(PLAYER_SUCCESS_MESSAGES.profileEdit))
        onSuccess?.()
        onClose()
        return
      }

      // 3. Player PUT + error mapping delegated to the shared submit core.
      //    Web saves the whole form (no `fields` filter).
      await submitPlayer(values, {
        playerId,
        initialValues,
        update: ({ playerId: pid, payload: updatePayload }) =>
          updatePlayer.mutateAsync({ playerId: pid, payload: updatePayload }),
        setErrors: (errs) => helpers.setErrors(errs),
        setTouched: (touched) => helpers.setTouched(touched, false),
        titleMap: BE_TO_FIELD_TITLE,
        notifySuccess: () =>
          message.success(
            formatSuccessToast(PLAYER_SUCCESS_MESSAGES.profileEdit)
          ),
        notifyError: (m) => message.error(m),
        // Bring the first errored field into view + focus it.
        onFieldErrors: () => scrollToFirstFieldError(),
        onSuccess: () => {
          onSuccess?.()
          onClose()
        },
      })
    } finally {
      helpers.setSubmitting(false)
    }
  }

  // Covers the cold fetch (skeleton) AND the full save flow — the image step and
  // the PUT — so Save/Cancel stay disabled across the whole sequential save and a
  // second click can't race an in-flight image upload before the PUT runs.
  const isLoading = isFetchLoading || isImagePending || updatePlayer.isPending

  return (
    <Formik<EditPlayerFormValues>
      key={formikKey}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}
    >
      <EditPlayerContext.Provider
        value={{
          isLoading,
          onCancel: onClose,
          playerId,
          salesforceId: data?.sf_id,
        }}
      >
        {children}
      </EditPlayerContext.Provider>
    </Formik>
  )
}
